import graphql__dc25b6c97193e4503e9f from "./__generated__/PostSignUpFormRoute_authenticationConfig.graphql.js";import { useState, useCallback, useEffect, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useFormatMessage } from "../Intl/Context";
import Modal from "../Modals/Modal";
import { intlMessages as signUpIntlMessages } from "../Authentication/AuthenticationSignUp";
import Answers from "../Answer/Answers";
import CheckBoxField from "../Form/Field/CheckBoxField";
import EmailTrackingConsentField from "../Form/Field/EmailTrackingConsentField";
import withFormStore, { useForm } from "../Form/withFormStore";
import Button from "../Buttons/Button";
import { postSignUpForm } from "../../mutations/PostSignUpFormMutation";
import { useNavigateTo } from "../Routes/RoutesContext";

import { unsetStoredLocation } from "../../reducers/location/index";
import { updateViewer } from "../../reducers/viewer";

const PostSignUpForm = props => {
  const authenticationConfig = useFragment(
    PostSignUpFormRoute_authenticationConfig,
    props.authenticationConfig
  );

  const {
    privacyPolicyPageUrl,
    enableEmailConsent,
    emailConsentLabel,
    userSubscriptionFormResponse
  } = authenticationConfig;

  const { answers } = userSubscriptionFormResponse || { answers: null };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { validateForm, formState } = useForm();

  const storedLocation = useSelector(({ location }) => location.storedLocation?.pathname);
  const dispatch = useDispatch();
  const navigateTo = useNavigateTo();

  // Avoid a redirect to this component URL to display the form if it was already filled
  // just before. This may happen if the user fills this form and then fills the
  // `ConfigureAccount` form : the user is redirected here after completing it, but we
  // don't want to show it again.
  const needsToFillPostSignUpForm = useSelector(
    ({ viewer }) => (viewer || {}).needsToFillPostSignUpForm
  );

  // Redirecting clears the stored location, which changes the dependencies of the effect
  // below and would run it again, sending the user to "/" instead of the stored location.
  // This guard makes sure the redirection happens only once.
  const hasRedirected = useRef(false);

  // This effect is the only place redirecting the user : submitting the form updates the
  // viewer, which makes `needsToFillPostSignUpForm` falsy and triggers it. Redirecting
  // from the submit response too would navigate twice.
  useEffect(() => {
    if (needsToFillPostSignUpForm || hasRedirected.current) return;

    hasRedirected.current = true;

    navigateTo(storedLocation || "/");
    dispatch(unsetStoredLocation());
  }, [needsToFillPostSignUpForm, navigateTo, storedLocation, dispatch]);

  const handleResponse = useCallback(
    ({ postSignUpForm }) => {
      setIsSubmitting(false);

      if (!postSignUpForm.success) return;

      dispatch(updateViewer(postSignUpForm.user));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      if (!validateForm()) return;

      setIsSubmitting(true);

      postSignUpForm(formState, handleResponse);
    },
    [validateForm, handleResponse, formState]
  );

  const t = useFormatMessage();
  const title = t(intlMessages.title);
  const description = t(intlMessages.description);
  const submit = t(intlMessages.submit);
  const gdprMention = t(signUpIntlMessages.gdprMention, {
    a: message => `<a href="${privacyPolicyPageUrl}" target="_blank">${message}</a>`
  });

  if (!needsToFillPostSignUpForm) return null;

  const formClassName = classNames(
    "authentication-fieldset",
    "authentication-post-sign-up",
    "box",
    "box-margin-top-mobile",
    "margin-bottom-xs",
    "padding-md",
    { loading: isSubmitting }
  );

  return (
    <Modal className="modal-with-actions" title={title}>
      <p className="box margin-bottom-xs padding-md">{description}</p>

      <form className={formClassName} onSubmit={handleSubmit} data-post-sign-up-form>
        <fieldset>
          <Answers answers={answers} />

          <div className="checkboxes-no-background">
            {enableEmailConsent && (
              <CheckBoxField fieldName="emailConsent" label={emailConsentLabel} />
            )}

            <EmailTrackingConsentField />
          </div>

          {privacyPolicyPageUrl && (
            <p
              className="text-muted text-smaller margin-bottom-sm margin-bottom-sm"
              dangerouslySetInnerHTML={{ __html: gdprMention }}
            />
          )}
        </fieldset>

        <Button
          type="submit"
          className="btn btn-primary btn-block-md"
          onClick={handleSubmit}
          label={submit}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          iconClassName="icon-arrow-right"
          iconPosition="after"
          data-sign-up-submit-button
        />
      </form>
    </Modal>
  );
};

const PostSignUpFormRoute_authenticationConfig = graphql__dc25b6c97193e4503e9f;

export default withFormStore(PostSignUpForm);

const intlMessages = defineMessages({
  title: {
    id: "postSignUpForm.title",
    defaultMessage: "Complétez votre profil"
  },
  description: {
    id: "postSignUpForm.description",
    defaultMessage: `
      Votre compte a bien été créé sur la plateforme ! Comme il s'agit de votre première
      connexion, nous vous invitons à répondre aux quelques questions suivantes afin de
      compléter votre profil.
    `
  },
  submit: {
    id: "postSignUpForm.submit",
    defaultMessage: "Compléter mon profil"
  }
});
